/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LogProvider, TrouterState } from './Interfaces';
import { TrouterManagerState }  from './InternalInterfaces';
import { Logger } from './Logger';
import { ReconnectReason } from './TrouterConnection';

export interface IManagerWorker {
    startFirstConnection(): void;
    stopFirstConnection(dontUnregister: boolean): void;

    startSecondConnection(useConnectParamsFromCache: boolean): void;
    stopSecondConnection(dontUnregister: boolean): void;
    stopSecondConnectionDelayed(): void;

    forceStopLingeringConnection(): void;

    switchConnections(): void;
    doesSecondConnectionExist(): boolean;

    dispatchConnected(): void;
    dispatchDisconnected(): void;
    dispatchTerminalError(): void;
    dispatchRegistrationState(isRegistered: boolean): void;

    expediteBackoffOnConnections(): void
}

/**
 * State machine for manager
 */
export class TrouterManagerFsm {
    private logger: Logger;
    private state: TrouterManagerState = TrouterManagerState.Unknown;

    constructor(logFunc: LogProvider, private worker: IManagerWorker) {
        this.logger = new Logger('ManagerFsm', logFunc);
    }

    public start() {
        if (this.state === TrouterManagerState.Unknown) {
            this.setState(TrouterManagerState.Disconnected);
            this.worker.forceStopLingeringConnection();
            this.worker.startFirstConnection();
        } else {
            // Trouter is already running, but external code calling start()
            // is often triggered by some relevant external event (e.g. computer waking up),
            // so we want to retry immediately to not create unnecessary delays.
            this.logger.info(`start called in state '${TrouterManagerState[this.state]}', expediting pending backoffs, if any`);
            this.worker.expediteBackoffOnConnections();
        }
    }

    public stop(dontUnregister?: boolean) {
        if (this.state !== TrouterManagerState.Unknown) {
            this.setState(TrouterManagerState.Unknown);
            this.worker.stopFirstConnection(dontUnregister === true);
            this.worker.stopSecondConnection(dontUnregister === true);
        } else {
            this.showIgnored('stop');
        }
    }

    /** Returns current state */
    public getState(): TrouterState {
        switch (this.state) {
        case TrouterManagerState.Unknown:
        case TrouterManagerState.Connected:
        case TrouterManagerState.Disconnected:
        case TrouterManagerState.Switching:
            return this.state as number as TrouterState;
        case TrouterManagerState.TerminalError:
            return TrouterState.Disconnected;
        }
    }

    public getInternalState(): TrouterManagerState {
        return this.state;
    }

    public onConnected(first: boolean) {
        if (this.state === TrouterManagerState.Disconnected && first) {
            if (this.worker.doesSecondConnectionExist()) {
                this.setState(TrouterManagerState.Switching);
            } else {
                this.setState(TrouterManagerState.Connected);
                this.worker.dispatchConnected();
            }
        } else {
            this.showIgnored(`onConnected(${first})`);
        }
    }

    public onRegistered(first: boolean) {
        if (this.state === TrouterManagerState.Disconnected && !first) {
            this.setState(TrouterManagerState.Connected);
            this.worker.switchConnections();
            this.worker.stopSecondConnection(true);
            this.worker.dispatchConnected();
        } else if (this.state === TrouterManagerState.Switching && !first) {
            this.setState(TrouterManagerState.Connected);
            this.worker.switchConnections();
            this.worker.stopSecondConnectionDelayed();
            this.worker.dispatchConnected();
        } else if(this.state === TrouterManagerState.Disconnected && first) { 
            // happens when reconnect prompts connection to the same instance which dispatches disconnect as duplicate (both on old connection)
            // at this point `first` is the new connection

            this.setState(TrouterManagerState.Connected);
            this.worker.dispatchConnected();
        }
        
        this.worker.dispatchRegistrationState(true);
    }

    public onUnregistered(first: boolean) {
        if (first) {
            this.worker.dispatchRegistrationState(false);
        }
    }

    public onReconnecting(first: boolean) {
        if ((this.state === TrouterManagerState.Connected || this.state === TrouterManagerState.Switching) && first) {
            this.setState(TrouterManagerState.Disconnected);
            this.worker.dispatchDisconnected();
        } else {
            this.showIgnored(`onReconnecting(${first})`);
        }
    }

    public onReconnectionRequired(first: boolean, useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        if (this.state === TrouterManagerState.Connected && first) {
            this.setState(TrouterManagerState.Switching);
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else if (this.state === TrouterManagerState.Disconnected && first) {
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else if (this.state === TrouterManagerState.Switching && first && reason === ReconnectReason.Configuration) {
            // Intentionally ignoring `trouter.reconnect` events arriving during an in-progress switch (on either connection),
            // only configuration change is handled. This matches the current behavior of the C++ client.
            // Even in the ignored cases, the client should still end up being connected to the right environment eventually.
            this.logger.debug('onReconnectionRequired: switch requested while already in Switching state');
            this.worker.stopSecondConnection(true);
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else {
            this.showIgnored(`onReconnectionRequired(${first})`);
        }
    }

    public onDisconnected(first: boolean) {
        if (this.state == TrouterManagerState.Unknown && first) {
            // This is executed after an explicit stop()
            this.worker.dispatchDisconnected();
        } else if (this.state == TrouterManagerState.Switching && first) {
            // The current/old connection got disconnected during a connection switch
            // (the disconnect would result only in onReconnecting() at other times,
            // when automatic reconnects are still enabled)
            this.worker.switchConnections();
            this.worker.stopSecondConnection(false);
            this.setState(TrouterManagerState.Disconnected);
            this.worker.dispatchDisconnected();
        } else {
            this.showIgnored(`onDisconnected(${first})`);
        }
    }

    public onTerminalError() {
        this.setState(TrouterManagerState.TerminalError);
        this.worker.dispatchTerminalError();
    }

    private showIgnored(event: string) {
        this.logger.info(`Ignoring event '${event}' in state '${TrouterManagerState[this.state]}'`);
    }

    private setState(state: TrouterManagerState) {
        this.logger.info(`Switching from state '${TrouterManagerState[this.state]}' to state '${TrouterManagerState[state]}'`);
        if (this.state === state) {
            this.logger.error(`Attempt to switch to the current state '${TrouterManagerState[state]}'`);

            return;
        }
        this.state = state;
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterManagerFsm.ts